import { makeAutoObservable, runInAction } from 'mobx';
import { api } from '@/api';
import type { MatchDto, RoundResultsDto } from '@/api/dto';
import type { RootStore } from './RootStore';

export class MatchStore {
  matchInfo: MatchDto | null = null;
  roundResults: RoundResultsDto | null = null;
  isLoading = false;

  constructor(_root: RootStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get nextRoundStartsAtUtc(): string | null {
    return this.matchInfo?.nextRoundStartsAtUtc ?? null;
  }

  get countdownMs(): number {
    if (!this.nextRoundStartsAtUtc) return 0;
    return Math.max(0, new Date(this.nextRoundStartsAtUtc).getTime() - Date.now());
  }

  async loadMatchInfo(code: string): Promise<void> {
    try {
      const info = await api.match.get(code);
      runInAction(() => { this.matchInfo = info; });
    } catch {
      // no match yet
    }
  }

  async loadRoundResults(code: string): Promise<void> {
    try {
      const results = await api.round.getResults(code);
      runInAction(() => { this.roundResults = results; });
    } catch {
      // no results yet
    }
  }

  applyMatchInfo(data: unknown): void {
    this.matchInfo = data as MatchDto;
  }

  applyResults(data: unknown): void {
    this.roundResults = data as RoundResultsDto;
  }

  applyBetweenRounds(nextRoundStartsAtUtc: string): void {
    if (this.matchInfo) {
      this.matchInfo = { ...this.matchInfo, status: 'BetweenRounds', nextRoundStartsAtUtc };
    }
  }

  applyMatchEnded(data: unknown): void {
    const m = data as { matchId?: string; status?: string; players?: unknown[] };
    if (this.matchInfo) {
      this.matchInfo = { ...this.matchInfo, status: 'Finished' };
    }
    void m;
  }

  reset() {
    this.matchInfo = null;
    this.roundResults = null;
  }
}
